import React, { useState, useEffect, useCallback } from "react";
import { Button } from "react-bootstrap";
import { FaPlus } from "react-icons/fa";

import StateTable from "../components/state/StateTable";
import StateForm from "../components/state/StateForm";

import {
    getStates,
    createState,
    updateState,
    deleteState
} from "../api/services/StateService";

const State = () => {
    const [states, setStates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Form state
    const [showForm, setShowForm] = useState(false);
    const [selectedState, setSelectedState] = useState(null);
    const [formLoading, setFormLoading] = useState(false);

    /* -------------------------------------------------------------------------- */
    /* FETCH STATES                                                               */
    /* -------------------------------------------------------------------------- */
    const fetchStates = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await getStates();
            setStates(response || []);
        } catch (err) {
            console.error("Error fetching states:", err);
            setError("Failed to load states");
            setStates([]);
        } finally {
            setLoading(false);
        }
    }, []);

    /* -------------------------------------------------------------------------- */
    /* CREATE / UPDATE HANDLER                                                     */
    /* -------------------------------------------------------------------------- */
    const handleSave = async (data) => {
        try {
            setFormLoading(true);

            if (selectedState) {
                await updateState(selectedState._id, data);
            } else {
                await createState(data);
            }

            setShowForm(false);
            setSelectedState(null);
            fetchStates();
        } catch (err) {
            console.error("Save failed:", err);
            setError(err.message || "Failed to save state");
        } finally {
            setFormLoading(false);
        }
    };

    /* -------------------------------------------------------------------------- */
    /* DELETE HANDLER                                                             */
    /* -------------------------------------------------------------------------- */
    const handleDelete = async (state) => {
        const confirmDelete = window.confirm(
            `Are you sure you want to delete ${state.name}?`
        );

        if (!confirmDelete) return;

        try {
            await deleteState(state._id);
            fetchStates();
        } catch (err) {
            console.error("Delete failed:", err);
            setError("Failed to delete state");
        }
    };

    /* -------------------------------------------------------------------------- */
    /* EDIT HANDLER                                                               */
    /* -------------------------------------------------------------------------- */
    const handleEdit = (state) => {
        setSelectedState(state);
        setShowForm(true);
    };

    useEffect(() => {
        fetchStates();
    }, [fetchStates]);

    /* -------------------------------------------------------------------------- */
    /* UI STATES                                                                  */
    /* -------------------------------------------------------------------------- */
    if (loading) {
        return (
            <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{ height: "50vh" }}
            >
                <div className="spinner-border text-primary" role="status" />
                <p className="mt-3">Loading states...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger mx-3 mt-3 d-flex justify-content-between align-items-center">
                <span>{error}</span>
                <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={fetchStates}
                >
                    Retry
                </Button>
            </div>
        );
    }

    /* -------------------------------------------------------------------------- */
    /* MAIN VIEW                                                                  */
    /* -------------------------------------------------------------------------- */
    return (
        <div>
            <header className="content-header d-flex justify-content-between align-items-center mb-2">
                <h1 className="mb-0">States</h1>

                <Button
                    variant="primary"
                    className="d-flex align-items-center"
                    onClick={() => {
                        setSelectedState(null);
                        setShowForm(true);
                    }}
                >
                    <FaPlus className="me-2" />
                    Add State
                </Button>
            </header>

            <StateTable
                data={states}
                pageSize={10}
                onDelete={handleDelete}
                onEdit={handleEdit}
            />

            {/* ---------------------------------------------------------------------- */}
            {/* STATE FORM MODAL                                                       */}
            {/* ---------------------------------------------------------------------- */}
            <StateForm
                show={showForm}
                onClose={() => {
                    setShowForm(false);
                    setSelectedState(null);
                }}
                onSubmit={handleSave}
                initialData={selectedState}
                loading={formLoading}
            />
        </div>
    );
};

export default State;
